import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Button, Card, Pagination, Input, Row, Col, Form, DatePicker, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import moment from "moment";
import { LotCodeOpsService } from '@gtpl/shared-services/production';
import  Highlighter  from 'react-highlight-words';
import { SearchOutlined } from '@ant-design/icons';
import { PriorityEnum, QualityTypeEnum } from '@gtpl/shared-models/common-models';
import { SortOrder } from 'antd/lib/table/interface';
import { LotInfoReportReq } from '@gtpl/shared-models/production-management';

const {RangePicker} = DatePicker
const {Option} = Select;

interface RowData {
  key: string;
  serialNumber: number;
  rowSpan: number;

  lotNumber: string;
  lotType: string;
  remarks: string;
  lotVariant?: string;
  lotQty?: string;
  priority?: string;
  exFactoryDate?: string;
  lotDate?: string;
  materialAssignDate?: string;

  workOrderNumber?: string;
  quality?: string;
  variant?: string;
  quantity?: string;
}

export const LotInfoReport = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [data,setData] = useState<any[]>([])
    const service = new LotCodeOpsService();
    const PAGE_SIZE = 20;
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);
    const [form] = Form.useForm()
    const [lotNumbers,setLotNumbers] = useState<any[]>([])
    const [workOrderNumbers,setWorkOrderNumbers] = useState<any[]>([])



   useEffect(() => {
        getLotInfo()
        getAllLotNumbers()
        getAllWorkOrderNumbers()
    },[])

    const getLotInfo = () => {
        const req = new LotInfoReportReq(localStorage.getItem('plantId') ? Number(localStorage.getItem('plantId')) : 0,null,null,null,null)
        if(form.getFieldValue('lotDate')){
            req.lotFromDate = form.getFieldValue('lotDate')[0].format('YYYY-MM-DD')
            req.lotToDate = form.getFieldValue('lotDate')[1].format('YYYY-MM-DD')
        }
        if(form.getFieldValue('lotNumber')){
            req.lotNumber = form.getFieldValue('lotNumber')
        }
        if(form.getFieldValue('workOrderNumber')){
            req.workOrderNumber = form.getFieldValue('workOrderNumber')
        }
        service.getLotInfo(req).then((res) => {
            if(res.status){
                setData(res.data)
            }else{
                setData([])
            }
        }).catch((err) => {
            setData([])    
        });
    }

    const getAllLotNumbers = () => {
        service.getLotNumberForLotInfo().then((res) => {
            if(res.status){
                setLotNumbers(res.data)
            }else{
                setLotNumbers([])
            }
        }).catch((err) => {
            setLotNumbers([])
        });                   
    }

        const getAllWorkOrderNumbers = () => {
        service.getWorkOrderNumberForLotInfo().then((res) => {
            if(res.status){
                setWorkOrderNumbers(res.data)
            }else{
                setWorkOrderNumbers([])
            }
        }).catch((err) => {
            setWorkOrderNumbers([])
        });                   
    }

/**
 * PAGINATION SHOULD HAPPEN
 * ON UNIQUE LOTS ONLY
 */
const paginatedLots = useMemo(() => {
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;

  return data.slice(startIndex, endIndex);
}, [data, currentPage]);

/**
 * FLATTEN ONLY PAGINATED LOTS
 */
const tableData = useMemo(() => {
  const rows: any[] = [];

  paginatedLots.forEach((lot: any, lotIndex: number) => {
    const materialRows =
      lot.materialInfo?.length > 0
        ? lot.materialInfo
        : [{}];

    materialRows.forEach(
      (material: any, materialIndex: number) => {
        rows.push({
          key: `${lot.lotNumber}-${materialIndex}`,

          /**
           * SERIAL NUMBER
           * BASED ON LOT ONLY
           */
          serialNumber:
            (currentPage - 1) * PAGE_SIZE +
            lotIndex +
            1,

          /**
           * ROWSPAN ONLY FOR FIRST ROW
           */
          rowSpan:
            materialIndex === 0
              ? materialRows.length
              : 0,

          lotNumber: lot.lotNumber,
          lotType: lot.lotType,
          remarks: lot.remarks,
          lotVariant: lot.lotVariant,
          lotQty: lot.lotQty,
          priority: lot.priority,
          exFactoryDate: lot.exFactoryDate,
          lotDate: lot.lotDate,
          materialAssignDate:
            lot.materialAssignDate,

          workOrderNumber:
            material.workOrderNumber,

          quality: material.quality,

          variant: material.variant,

          quantity: material.quantity,
        });
      }
    );
  });

  return rows;
}, [paginatedLots, currentPage]);

  /**
   * COMMON ROWSPAN FUNCTION
   */
  const renderMergedCell = (
    value: any,
    row: RowData
  ) => {
    return {
      children: value || "-",
      props: {
        rowSpan: row.rowSpan,
      },
    };
  };

  const exportExcel = () => {
  const wb = XLSX.utils.book_new();

  const wsData: any[][] = [];

  /**
   * HEADERS
   */
  wsData.push([
    "S No",
    "Lot Date",
    "Lot Number",
    "Lot Type",
    "Remarks",
    "Ex-Factory Date",
    "Priority",
    "Material Assigned Date",
    "Variant",
    "Quantity",
    "Work Order Number",
    "Work Order Variant",
    "Work Order Quality",
    "Work Order Quantity",
  ]);

  /**
   * MERGES
   */
  const merges: any[] = [];

  let currentRow = 1;

  data.forEach((lot, lotIndex) => {
    const materials =
      lot.materialInfo?.length > 0
        ? lot.materialInfo
        : [{}];

    const startRow = currentRow;

    materials.forEach((mat, matIndex) => {
      wsData.push([
        matIndex === 0
          ? lotIndex + 1
          : "",

        matIndex === 0
          ? lot.lotDate
            ? moment(
                lot.lotDate
              ).format("YYYY-MM-DD")
            : "-"
          : "",

        matIndex === 0
          ? lot.lotNumber
          : "",

        matIndex === 0
          ? lot.lotType
          : "",

        matIndex === 0
          ? lot.remarks
          : "",

        matIndex === 0
          ? lot.exFactoryDate
            ? moment(
                lot.exFactoryDate
              ).format("YYYY-MM-DD")
            : "-"
          : "",

        matIndex === 0
          ? lot.priority || "-"
          : "",

        matIndex === 0
          ? lot.materialAssignDate
            ? moment(
                lot.materialAssignDate
              ).format("YYYY-MM-DD")
            : "-"
          : "",

        matIndex === 0
          ? lot.lotVariant || "-"
          : "",

        matIndex === 0
          ? Number(lot.lotQty) || 0
          : "",

        mat.workOrderNumber || "-",
        mat.variant || "-",
        mat.quality || "-",
        Number(mat.quantity) || "-",
      ]);

      currentRow++;
    });

    /**
     * MERGE PARENT COLUMNS
     */
    if (materials.length > 1) {
      for (let col = 0; col <= 9; col++) {
        merges.push({
          s: {
            r: startRow,
            c: col,
          },
          e: {
            r: currentRow - 1,
            c: col,
          },
        });
      }
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  /**
   * APPLY MERGES
   */
  ws["!merges"] = merges;

  /**
   * COLUMN WIDTHS
   */
  ws["!cols"] = [
    { wch: 10 },
    { wch: 15 },
    { wch: 18 },
    { wch: 15 },
    { wch: 25 },
    { wch: 18 },
    { wch: 12 },
    { wch: 22 },
    { wch: 18 },
    { wch: 14 },
    { wch: 22 },
    { wch: 22 },
    { wch: 22 },
    { wch: 22 },
  ];

  /**
   * HEADER STYLE
   */
  for (let col = 0; col < 14; col++) {
    const cellAddress =
      XLSX.utils.encode_cell({
        r: 0,
        c: col,
      });

    ws[cellAddress].s = {
      fill: {
        fgColor: {
          rgb: "FFFF00",
        },
      },

      font: {
        bold: true,
        color: {
          rgb: "000000",
        },
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },

      border: {
        top: {
          style: "thin",
          color: { rgb: "000000" },
        },
        bottom: {
          style: "thin",
          color: { rgb: "000000" },
        },
        left: {
          style: "thin",
          color: { rgb: "000000" },
        },
        right: {
          style: "thin",
          color: { rgb: "000000" },
        },
      },
    };
  }

  /**
   * BODY STYLE
   */
  const range =
    XLSX.utils.decode_range(ws["!ref"]!);

  for (
    let row = 1;
    row <= range.e.r;
    row++
  ) {
    for (
      let col = 0;
      col <= range.e.c;
      col++
    ) {
      const cell =
        XLSX.utils.encode_cell({
          r: row,
          c: col,
        });

      if (!ws[cell]) continue;

      ws[cell].s = {
        alignment: {
          vertical: "center",
          horizontal:
            col <= 9
              ? "center"
              : "left",
          wrapText: true,
        },

        border: {
          top: {
            style: "thin",
            color: { rgb: "D9D9D9" },
          },
          bottom: {
            style: "thin",
            color: { rgb: "D9D9D9" },
          },
          left: {
            style: "thin",
            color: { rgb: "D9D9D9" },
          },
          right: {
            style: "thin",
            color: { rgb: "D9D9D9" },
          },
        },
      };
    }
  }

  /**
   * ROW HEIGHT
   */
  ws["!rows"] = wsData.map(() => ({
    hpt: 25,
  }));

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    "Lot Info Report"
  );

  const excelBuffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob(
    [excelBuffer],
    {
      type: "application/octet-stream",
    }
  );

  saveAs(blob, "Lot_Info_Report.xlsx");
};


      function handleSearch(selectedKeys, confirm, dataIndex) {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    function handleReset(clearFilters) {
        clearFilters();
        setSearchText('');
    };
    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        filterIcon: filtered => (
            <SearchOutlined type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes(value.toLowerCase())
                : false,
        onFilterDropdownVisibleChange: visible => {
            if (visible) { setTimeout(() => searchInput.current.select()); }
        },
        render: text =>
            text ? (
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text.toString()}
                    />
                ) : text
            ) : null
    });

  const columns: ColumnsType<RowData> = [
    {
      title: "S No",
      dataIndex: "serialNumber",
      width: 80,
      align: "center",
      render: (_, row) =>
        renderMergedCell(row.serialNumber, row),
    },

    {
      title: "Lot Date",
      dataIndex: "lotDate",
      width: 120,
      sorter: (a, b) => moment(a.lotDate).unix() - moment(b.lotDate).unix(),
        sortDirections: ['descend', 'ascend'],
        ...getColumnSearchProps('lotDate'),
      render: (_, row) =>
        renderMergedCell(
          row.lotDate
            ? moment(row.lotDate).format(
                "YYYY-MM-DD"
              )
            : "-",
          row
        ),
    },

    {
      title: "Lot Number",
      dataIndex: "lotNumber",
      width: 150,
      sorter: (a, b) => a.lotNumber.length - b.lotNumber.length || a.lotNumber.localeCompare(b.lotNumber),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('lotNumber'),
      render: (_, row) =>
        renderMergedCell(row.lotNumber, row),
    },

    {
      title: "Lot Type",
      dataIndex: "lotType",
      width: 120,
      sorter: (a, b) => a.lotType.length - b.lotType.length || a.lotType.localeCompare(b.lotType),
            sortDirections: ['descend', 'ascend'],
            filters:[
                {text:'Carding',value:'Carding'},
                {text:'Dressing',value:'Dressing'},
                {text:'BALE',value:'BALE'}
            ],
            filterMultiple: true,
            onFilter: (value, record) => {
                return record.lotType == value;
            },
      render: (_, row) =>
        renderMergedCell(row.lotType, row),
    },

    {
      title: "Remarks",
      dataIndex: "remarks",
      width: 200,
       sorter: (a, b) => a.remarks.length - b.remarks.length || a.remarks.localeCompare(b.remarks),
            sortDirections: ['descend', 'ascend'],
      render: (_, row) =>
        renderMergedCell(row.remarks, row),
    },

    {
      title: "Ex-Factory Date",
      dataIndex: "exFactoryDate",
      width: 150,
      sorter: (a, b) => moment(a.exFactoryDate).unix() - moment(b.exFactoryDate).unix(),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('exFactoryDate'),
      render: (_, row) =>
        renderMergedCell(
          row.exFactoryDate
            ? moment(
                row.exFactoryDate
              ).format("YYYY-MM-DD")
            : "-",
          row
        ),
    },

    {
      title: "Priority",
      dataIndex: "priority",
      width: 100,
      sorter: (a, b) => a.priority?.length - b.priority?.length || a.priority?.localeCompare(b.priority),
            sortDirections: ['descend', 'ascend'],
            filters: Object.values(PriorityEnum).map(val => ({
                text: val,
                value: val
            })),
            filterMultiple: true,
            onFilter: (value, record) => {
                return record.priority == value;
            },
      render: (_, row) =>
        renderMergedCell(row.priority || "-", row),
    },

    {
      title: "Material Assigned Date",
      dataIndex: "materialAssignDate",
      width: 180,
      sorter: (a, b) => moment(a.materialAssignDate).unix() - moment(b.materialAssignDate).unix(),
                sortDirections: ['descend', 'ascend'] as SortOrder[],
                ...getColumnSearchProps('materialAssignDate'),
      render: (_, row) =>
        renderMergedCell(
          row.materialAssignDate
            ? moment(
                row.materialAssignDate
              ).format("YYYY-MM-DD")
            : "-",
          row
        ),
    },

    {
      title: "Variant",
      dataIndex: "lotVariant",
      width: 150,
      sorter: (a, b) => a.lotNumber.length - b.lotNumber.length || a.lotNumber.localeCompare(b.lotNumber),
                sortDirections: ['descend', 'ascend'],
                ...getColumnSearchProps('lotNumber'),
      render: (_, row) =>
        renderMergedCell(
          row.lotVariant || "-",
          row
        ),
    },

    {
      title: "Quantity",
      dataIndex: "lotQty",
      width: 120,
      sorter: (a, b) => (Number(a.lotQty) || 0) - (Number(b.lotQty) || 0),
      sortDirections: ['descend', 'ascend'],
      render: (_, row) =>
        renderMergedCell(
          Number(row.lotQty) || 0,
          row
        ),
    },

    {
      title: "Work Order Number",
      dataIndex: "workOrderNumber",
      width: 180,
    //    sorter: (a, b) => a.workOrderNumber.length - b.workOrderNumber.length || a.workOrderNumber.localeCompare(b.workOrderNumber),
    //     sortDirections: ['descend', 'ascend'],
        // ...getColumnSearchProps('workOrderNumber'),
        render:(text,record) => {return text ?? '-'}
    },

    {
      title: "Work Order Variant",
      dataIndex: "variant",
      width: 180,
    //    sorter: (a, b) => a.variant.length - b.variant.length || a.variant.localeCompare(b.variant),
    //         sortDirections: ['descend', 'ascend'],
            // ...getColumnSearchProps('variant'),
        render:(text,record) => {return text ?? '-'}

    },

    {
      title: "Work Order Quality",
      dataIndex: "quality",
      width: 180,
    //    sorter: (a, b) => a.quality.length - b.quality.length || a.quality.localeCompare(b.quality),
    //         sortDirections: ['descend', 'ascend'],
            // filters:Object.values(QualityTypeEnum).map(val => ({
            //     text: val,
            //     value: val
            // })),
            // filterMultiple: true,
            // onFilter: (value, record) => {
            //     return record.quality == value;
            // },
        render:(text,record) => {return text ?? '-'}

    },

    {
      title: "Work Order Quantity",
      dataIndex: "quantity",
      width: 180,
    //    sorter: (a, b) => (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
    //         sortDirections: ['descend', 'ascend'],
        render:(text,record) => {return Number(text) ?? '-'}

    },
  ];

    const onReset = () => {
        form.resetFields()
        getLotInfo()
    }



    return (

        <div>
            <style>
                {
                    `.custom-table .ant-table-tbody > tr > td {
                        background: #ffffff !important;
                        }
                    `
                }
            </style>
        <Card title={<span style={{ color: 'white' }}>Lot Info Report</span>} style={{ textAlign: 'center' }} headStyle={{ backgroundColor: '#69c0ff', border: 0 }} extra={<Button onClick={exportExcel}>Export Excel</Button>}>
        <Form form={form}>
            <Row gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                    <Form.Item name='lotDate' label='Lot Date'>
                        <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                 <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                    <Form.Item name='lotNumber' label='Lot Number'>
                       <Select allowClear showSearch placeholder='Select Lot Number' optionFilterProp='children' mode='multiple'>
                        {
                            lotNumbers.map(e => (
                                <Option key={e.lotNumber} value={e.lotNumber}>{e.lotNumber}</Option>
                            ))  
                        }
                       </Select>
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                    <Form.Item name='workOrderNumber' label='Work Order Number'>
                       <Select allowClear showSearch placeholder='Select Work Order Number' optionFilterProp='children' mode='multiple'>
                        {
                            workOrderNumbers.map(e => (
                                <Option key={e.workOrderNumber} value={e.workOrderNumber}>{e.workOrderNumber}</Option>
                            ))  
                        }
                       </Select>
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 2 }}>
                    <Form.Item>
                        <Button type='primary' onClick={getLotInfo} >Search</Button>
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 2 }}>
                    <Form.Item>
                        <Button onClick={onReset}>Reset</Button>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
            <Table className="custom-table" bordered columns={columns} dataSource={tableData} pagination={false} size="small" />

            <div
            style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
            }}
            >
            <Pagination
                current={currentPage}
                pageSize={PAGE_SIZE}
                total={data.length}
                onChange={(page) => {
                setCurrentPage(page);
                }}
                showSizeChanger={false}
            />
            </div>

        </Card>

        </div>
    )
}   

export default LotInfoReport;